import { cn } from '@/lib/utils';

import { WithRef } from '@/cms/types/types';
import { gradientDecodoBorderStyling } from '@/styles/gradient';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  roudnessLevel?: keyof typeof roundnessClasses;
  /** for backwards compatibility. Can likely be removed once rebranding is done and use selectability as the driver*/
  hasHoverEffect?: boolean;

  /** Prop added during decodo redesign for when a card needs to be selectable. See component for details. */
  hasShadow?: boolean;

  color?: 'neutral' | 'gradient';
  padSize?: PadSize;

  classNames?: {
    root: string;
    hover?: string;
  };
}

export const padSizeOptions = ['s', 'm', 'l'] as const;
type PadSize = (typeof padSizeOptions)[number];
export const padMapping: { [K in PadSize]: `p-${string}` } = {
  s: 'p-4',
  m: 'p-6',
  l: 'p-9',
};

export const roudnessOptions = ['s', 'm', 'l', 'xl'] as const;
type Roundness = (typeof roudnessOptions)[number];

const roundnessClasses: Record<Roundness, `rounded${string}`> = {
  s: 'rounded',
  m: 'rounded-xl',
  l: 'rounded-3xl',
  xl: 'rounded-[2.25rem]',
};

export const commonStyles = cn(
  'relative overflow-hidden border border-solid p-4 transition-colors',
  'bg-base-white',
  'text-base-black',
  'dark:bg-gray-50',
  'dark:border-gray-400'
);

const Card = ({
  children,
  roudnessLevel = 's',
  padSize = 'm',
  hasHoverEffect = false,
  color = 'neutral',
  classNames,
  hasShadow = false,
  ref,
  ...props
}: WithRef<HTMLDivElement, CardProps>) => {
  const padSizeClassName = padMapping[padSize];

  const hoverClasses = cn(
    'cursor-pointer',
    'hover:border-brand-500 dark:hover:border-brand-500',
    classNames?.hover
  );
  const shadowClasses = cn('shadow-card-d dark:shadow-none');

  const colorVariantClasses = {
    gradient: `${gradientDecodoBorderStyling({
      borderWidth: 'p-px',
      color: 'gradient-main',
      borderRadius: roundnessClasses[roudnessLevel],
    })}`,
    neutral: '',
  };

  return (
    <div
      {...props}
      className={cn(
        commonStyles,
        hasShadow && shadowClasses,
        colorVariantClasses[color],
        hasHoverEffect && hoverClasses,
        padSizeClassName,
        roundnessClasses[roudnessLevel],
        classNames?.root
      )}
      ref={ref}
      data-tracker-component='card'
    >
      {children}
    </div>
  );
};

export default Card;
